// components/BusinessInfo.tsx
import React, { useState } from 'react';

interface BusinessInfoProps {
  businessName?: string;
  businessDescription?: string;
  businessAddress?: string;
  maxDescriptionLength?: number;
  /**
   * Esconde la tarjeta de ubicación en pantallas chicas. Se usa cuando abajo
   * ya se muestran las sedes en solapas (FichaMovil): ahí van las direcciones
   * reales de cada consultorio, así que repetir esta sería decir lo mismo
   * dos veces y peor.
   */
  ocultarUbicacionEnMovil?: boolean;
}

const BusinessInfo: React.FC<BusinessInfoProps> = ({
  businessName,
  businessDescription,
  businessAddress,
  maxDescriptionLength = 200,
  ocultarUbicacionEnMovil = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!businessDescription && !businessAddress) {
    return null;
  }

  const shouldTruncate = businessDescription && businessDescription.length > maxDescriptionLength;
  const displayDescription = shouldTruncate && !isExpanded
    ? businessDescription.slice(0, maxDescriptionLength) + '...'
    : businessDescription;

  return (
    <div className="space-y-4">
      {/* Descripción del negocio */}
      {businessDescription && (
        <div className="relative">
          {/* Alineada a la izquierda y más oscura (oct-2026): centrada y en
              gris claro costaba leerla; justificada, en un celular deja
              huecos desparejos entre palabras. */}
          <p className="text-slate-700 leading-relaxed text-left">
            {displayDescription}
          </p>
          
          {shouldTruncate && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="mt-2 text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center space-x-1 transition-colors"
            >
              <span>{isExpanded ? 'leer menos' : 'leer más'}</span>
              <svg 
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Dirección del negocio - clickeable para abrir en Google Maps */}
      {businessAddress && (
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessAddress)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`${ocultarUbicacionEnMovil ? 'hidden lg:flex' : 'flex'} items-start space-x-3 p-3 bg-gray-50 hover:bg-blue-50 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors group`}
          title="Abrir en Google Maps"
        >
          <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium text-gray-900">Ubicación</p>
              <svg className="w-3.5 h-3.5 text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </div>
            <p className="text-sm text-gray-600 group-hover:text-blue-700 transition-colors">{businessAddress}</p>
            <p className="text-[11px] text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity mt-0.5">
              Abrir en Google Maps
            </p>
          </div>
        </a>
      )}
    </div>
  );
};

export default BusinessInfo;